import { Box, CircularProgress, Divider, Typography, useTheme } from '@mui/material';
import Paper from 'components/paper';
import { Play } from 'lucide-react';

import Collapsible from 'components/collapsible';
import { useAccountState } from 'contexts/account/account-context';
import { ErrorHandlerContextActionTypes, useErrorHandlerContextDispatch } from 'contexts/error-handler/ErrorHandlerContext';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { runScenario } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-run';
import StepFooter from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-footer';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { ScenarioSetupStepProps } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-setup-step-props.type';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';

import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { useMemo } from 'react';
import { FormattedDate, FormattedNumber, useIntl } from 'react-intl';
import titleCase from 'utils/title-case';
import { combinedFeeds, formatCurrencyPerUnit } from 'views/bluviu-nutrition/features/scenarios-v2/utils/feed-program-utils';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';
import { AxiosResponse } from 'axios';
import R2ScenarioProgress from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/r2-scenario-progress';
import _ from 'underscore';
import { ScenarioModel } from 'types/shared/scenario-model-type.enum';
import { addDatadogRumAction } from 'utils/datadog-rum';

const fishTableHeaders = ['Name', 'Stocking date', 'Scenario start', 'No. of fish', 'Avg. weight', 'Mortality profile'];

const OverviewSection = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  if (!scenario) return null;

  const overviewItems: { label: string; value: React.ReactNode }[] = [
    { label: 'Scenario name', value: scenario.name },
    { label: 'Generation', value: scenario.generation?.displayName },
    { label: 'Model', value: titleCase(scenario.model) },
    { label: 'Calibration', value: scenario.calibrationName },
    { label: 'Mortality profile', value: scenario.scenarioDefaults?.mortalityProfileName },
    { label: 'Imported fish groups', value: scenario.importedFishGroupsCount },
    { label: 'Start date', value: <FormattedDate value={scenario.scenarioStart} year="numeric" month="2-digit" day="2-digit" /> },
    // Missing last imported date. Where does this come from? It is not on the scenario object.
    // Missing mortality profile. Where does this come from? It is not on the scenario object.
    { label: 'End date', value: <FormattedDate value={scenario.endDate} year="numeric" month="2-digit" day="2-digit" /> },
  ];

  return (
    <Collapsible label="Overview" open hideBorder backgroundColor={theme.palette.blue.o25} marginBottom={0}>
      <Box display="flex" flexDirection="column" gap={2}>
        {overviewItems.map((item) => (
          <Box key={item.label} display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={'bold'}>{item.label}:</Typography>
            <Typography>{item.value}</Typography>
          </Box>
        ))}
      </Box>
    </Collapsible>
  );
};

const FishDataSection = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  if (!scenario) return null;

  const options: HeadCell[] = [
    { id: 'fishGroupName', label: 'Name', align: 'left' },
    { id: 'unitName', label: 'Unit name', align: 'left' },
    { id: 'stockingDate', label: 'Stocking date', align: 'left' },
    { id: 'startDate', label: 'Scenario start', align: 'left' },
    { id: 'fishCount', label: 'No. of fish', align: 'right' },
    { id: 'meanWeight', label: 'Avg. weight', align: 'right' },
  ];
  const [includedCages] = useMemo(() => _.partition(scenario.cageData || [], (cage) => cage.isIncluded), [scenario.cageData]);

  return (
    <Collapsible label="Fish data" open hideBorder backgroundColor={theme.palette.blue.o25} marginBottom={0}>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box display="flex" flexDirection="column" gap={2}>
          <List options={options} dark>
            {includedCages.map((cage) => (
              <ListRow key={cage.fishGroupId}>
                <ListCell>{cage.fishGroupName}</ListCell>
                <ListCell>{cage.unitName}</ListCell>
                <ListCell>
                  <FormattedDate value={cage.stockingDate} year="numeric" month="2-digit" day="2-digit" />
                </ListCell>
                <ListCell>
                  <FormattedDate value={cage.startDate} year="numeric" month="2-digit" day="2-digit" />
                </ListCell>
                <ListCell align="right">
                  <FormattedNumber value={cage.fishCount} />
                </ListCell>
                <ListCell align="right">
                  <FormattedNumber value={cage.meanWeight} />
                </ListCell>
              </ListRow>
            ))}
          </List>
        </Box>
      </Box>
    </Collapsible>
  );
};

const ExcludedFishDataSection = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  if (!scenario) return null;

  const options: HeadCell[] = [
    { id: 'fishGroupName', label: 'Name', align: 'left' },
    { id: 'unitName', label: 'Unit name', align: 'left' },
    { id: 'stockingDate', label: 'Stocking date', align: 'left' },
    { id: 'startDate', label: 'Scenario start', align: 'left' },
    { id: 'fishCount', label: 'No. of fish', align: 'right' },
    { id: 'meanWeight', label: 'Avg. weight', align: 'right' },
  ];

  const [, excludedCages] = useMemo(() => _.partition(scenario.cageData || [], (cage) => cage.isIncluded), [scenario.cageData]);

  return (
    <Collapsible label="Excluded Fish data" open={false} hideBorder backgroundColor={theme.palette.darkBlue.o25} marginBottom={0}>
      <Box display="flex" flexDirection="column" gap={2}>
        <List options={options} dark sx={{ backgroundColor: theme.palette.darkBlue.o25 }}>
          {excludedCages.map((cage) => (
            <ListRow
              key={cage.fishGroupId}
              sx={{ '& .MuiTableCell-body': { backgroundColor: `${theme.palette.darkBlue.o25} !important` } }}>
              <ListCell>{cage.fishGroupName}</ListCell>
              <ListCell>{cage.unitName}</ListCell>
              <ListCell>
                <FormattedDate value={cage.stockingDate} year="numeric" month="2-digit" day="2-digit" />
              </ListCell>
              <ListCell>
                <FormattedDate value={cage.startDate} year="numeric" month="2-digit" day="2-digit" />
              </ListCell>
              <ListCell align="right">
                <FormattedNumber value={cage.fishCount} />
              </ListCell>
              <ListCell align="right">
                <FormattedNumber value={cage.meanWeight} />
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Box>
    </Collapsible>
  );
};

const FeedProgramsSection = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const feedsByProgram = useMemo(
    () =>
      scenario?.feedProgram.map((program) => ({
        id: program.id,
        name: program.name,
        feeds: combinedFeeds(program.manualFeeds, program.bestMixFeeds),
      })) ?? [],
    [scenario?.feedProgram]
  );

  const scenarioCurrency = useMemo(() => {
    return formatCurrencyPerUnit(scenario?.financialData.currencyPerUnit);
  }, [scenario?.financialData.currencyPerUnit]);

  if (!scenario) return null;

  return (
    <Collapsible label="Feed programs" open hideBorder backgroundColor={theme.palette.blue.o25} marginBottom={0}>
      <Box display="flex" flexDirection="column" gap={2}>
        {feedsByProgram.map((program, idx) => (
          <Box key={program.id} display="flex" flexDirection="column" gap={4}>
            <Typography fontWeight={'bold'}>{program.name}:</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 0fr',
                columnGap: 2,
                rowGap: 1,
                textAlign: 'left',
              }}>
              {program.feeds.map((feed, feedIdx) => (
                <>
                  <Typography variant="body2">{feed.fishSizeStart}g:</Typography>
                  <Typography variant="body2">{feed.name}</Typography>
                  <Typography variant="body2">
                    {feed.price}&nbsp;{scenarioCurrency}
                  </Typography>
                </>
              ))}
            </Box>
            {idx < feedsByProgram.length - 1 && (
              <Divider
                flexItem
                sx={{
                  fontWeight: 'bold',
                }}
                variant="middle"
              />
            )}
          </Box>
        ))}
      </Box>
    </Collapsible>
  );
};

const FinanceSection = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  if (!scenario) return null;

  const { financialData } = scenario;

  const {
    treatmentsPerCage,
    costOfSingleTreatment,
    productionCostPerWeekPerCage,
    harvestCostPerKg,
    smoltCostPerSingleSmolt,
    salesPricePerKgUnder1Kg,
    salesPricePerKg1To2Kg,
    salesPricePerKg2To3Kg,
    salesPricePerKg3To4Kg,
    salesPricePerKg4To5Kg,
    salesPricePerKg5To6Kg,
    salesPricePerKgOver6Kg,
    currency,
  } = financialData;

  const formattedCurrency = currency.toUpperCase();

  const financeItems = [
    { label: 'Treatments / cage', value: <FormattedNumber value={treatmentsPerCage} /> },
    {
      label: 'Cost per treatment',
      value: (
        <Typography>
          <FormattedNumber value={costOfSingleTreatment} /> {formattedCurrency}
        </Typography>
      ),
    },
    {
      label: 'Production cost / week / cage',
      value: (
        <Typography>
          <FormattedNumber value={productionCostPerWeekPerCage} /> {formattedCurrency}
        </Typography>
      ),
    },
    {
      label: 'Harvest cost / kg',
      value: (
        <Typography>
          <FormattedNumber value={harvestCostPerKg} /> {formattedCurrency}
        </Typography>
      ),
    },
    {
      label: 'Smolt cost / smolt',
      value: (
        <Typography>
          <FormattedNumber value={smoltCostPerSingleSmolt} /> {formattedCurrency}
        </Typography>
      ),
    },
  ];

  return (
    <Collapsible label="Finance" open hideBorder backgroundColor={theme.palette.blue.o25} marginBottom={0}>
      <Box display="flex" flexDirection="column" gap={2}>
        {financeItems.map((item, idx) => (
          <Box key={item.label} display="flex" justifyContent="space-between">
            <Typography fontWeight={'bold'}>{item.label}:</Typography>
            <Typography>{item.value}</Typography>
          </Box>
        ))}
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography fontWeight={'bold'}>Sales price per size (/ kg HOG)</Typography>
          {[
            { label: 'Under 1 kg', value: salesPricePerKgUnder1Kg },
            { label: '1 - 2 kg', value: salesPricePerKg1To2Kg },
            { label: '2 - 3 kg', value: salesPricePerKg2To3Kg },
            { label: '3 - 4 kg', value: salesPricePerKg3To4Kg },
            { label: '4 - 5 kg', value: salesPricePerKg4To5Kg },
            { label: '5 - 6 kg', value: salesPricePerKg5To6Kg },
            { label: 'Over 6 kg', value: salesPricePerKgOver6Kg },
          ].map((item, idx) => (
            <Box key={idx} display="flex" justifyContent="space-between">
              <Typography>{item.label}:</Typography>
              <Typography>{item.value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Collapsible>
  );
};

const StepReviewAndRun: React.FC<ScenarioSetupStepProps> = ({ onClick, activeStep, isLastStep }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const params = useParams<R2ScenarioParams>();
  const snackbarDispatch = useSnackbarDispatch();
  const errorHandlerDispatch = useErrorHandlerContextDispatch();

  const {
    r2ScenarioQuery: { data: scenario },
    r2ScenarioStatusQuery: { refetch: refetchScenarioStatus, data: scenarioStatus },
  } = useR2ScenarioState();

  const submitScenario = usePost<AxiosResponse, void>(
    (request) => runScenario(String(account.company?.companyId), params.scenarioId),
    (response) => {
      addDatadogRumAction('scenario_run_submitted', {
        scenario_model: ScenarioModel.EFFECTIVE_ENERGY_GT_2_MULTI,
      });

      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.scenarioRunStarted' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getR2Scenario']
      });

      queryClient.invalidateQueries({
        queryKey: ['getR2Scenarios', params.globalSiteId],
        exact: true
      });

      refetchScenarioStatus();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      errorHandlerDispatch({
        type: ErrorHandlerContextActionTypes.ADD_ERRORS,
        payload: {
          errors: [
            {
              code: error.response?.status.toString(),
              message: error.response?.data as string,
            },
          ],
        },
      });
    }
  );

  const handlePostScenario = () => submitScenario.mutate();

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography>Review and run</Typography>
      <Divider />
      <R2ScenarioProgress />
      <Box display={'flex'} flexDirection={'column'} gap={2}>
        <OverviewSection />
        <FishDataSection />
        <ExcludedFishDataSection />
        <FeedProgramsSection />
        <FinanceSection />
      </Box>
      {scenario?.state !== ScenarioState.SCENARIO_RESULT_SENT && (
        <>
          <Divider />
          <StepFooter
            onPrev={() => onClick(activeStep - 1)}
            onNext={handlePostScenario}
            disablePrev={
              submitScenario.isPending ||
              scenarioStatus?.scenarioIsRunning ||
              [ScenarioState.SUBMITTED, ScenarioState.COMPLETED].includes(scenario?.state as ScenarioState)
            }
            disableNext={
              submitScenario.isPending ||
              scenarioStatus?.scenarioIsRunning ||
              [ScenarioState.SUBMITTED, ScenarioState.COMPLETED].includes(scenario?.state as ScenarioState)
            }
            nextLabel={
              isLastStep ? (submitScenario.isPending || scenarioStatus?.scenarioIsRunning ? 'Running...' : 'Run scenario') : undefined
            }
            nextIcon={
              isLastStep ? (
                submitScenario.isPending || scenarioStatus?.scenarioIsRunning ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Play />
                )
              ) : undefined
            }
          />
        </>
      )}
    </Paper>
  );
};

export default StepReviewAndRun;
